<!-- UploadResultModal.vue -->
<template>
  <div class="modal fade upload-result-modal" tabindex="-1" role="dialog" aria-labelledby="uploadResultModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header" :class="uploadResult.failed > 0 ? 'bg-warning' : 'bg-success'">
          <h5 class="modal-title text-white" id="uploadResultModalLabel">
            <i class="bx bx-cloud-upload me-2"></i>
            Upload Results
          </h5>
          <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <!-- Summary Statistics -->
          <div class="row mb-4">
            <div class="col-md-4">
              <div class="card border-success">
                <div class="card-body text-center">
                  <h3 class="text-success mb-0">{{ uploadResult.successful || 0 }}</h3>
                  <p class="text-muted mb-0">Successfully Uploaded</p>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="card border-danger">
                <div class="card-body text-center">
                  <h3 class="text-danger mb-0">{{ uploadResult.failed || 0 }}</h3>
                  <p class="text-muted mb-0">Failed</p>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="card border-primary">
                <div class="card-body text-center">
                  <h3 class="text-primary mb-0">{{ uploadResult.totalProcessed || 0 }}</h3>
                  <p class="text-muted mb-0">Total Processed</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Upload Message -->
          <div v-if="uploadResult.message" class="alert" :class="uploadResult.failed > 0 ? 'alert-warning' : 'alert-success'">
            <i class="bx bx-info-circle me-2"></i>
            {{ uploadResult.message }}
          </div>

          <!-- Failed Records Section -->
          <div v-if="uploadResult.failed > 0 && uploadResult.failedRecords && uploadResult.failedRecords.length > 0">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="mb-0 text-danger">
                <i class="bx bx-error me-2"></i>
                Failed Records ({{ uploadResult.failedRecords.length }})
              </h6>
              <button @click="downloadFailedRecordsCSV" class="btn btn-sm btn-outline-danger">
                <i class="bx bx-download me-1"></i>
                Download Failed Records (CSV)
              </button>
            </div>

            <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
              <table class="table table-sm table-hover table-bordered">
                <thead class="table-danger sticky-top">
                <tr>
                  <th style="width: 60px;">Row #</th>
                  <th>UPN</th>
                  <th>Plot No</th>
                  <th>Owner Names</th>
                  <th>ID Number</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Failure Reason</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(record, index) in paginatedFailedRecords" :key="index">
                  <td>{{ record.rowNumber }}</td>
                  <td>{{ record.upn || '-' }}</td>
                  <td>{{ record.plotNo || '-' }}</td>
                  <td>{{ record.ownerNames || '-' }}</td>
                  <td>{{ record.idNo || '-' }}</td>
                  <td>{{ record.ownerPhoneNo || '-' }}</td>
                  <td>
                      <span class="badge" :class="getStatusBadgeClass(record.status)">
                        {{ record.status }}
                      </span>
                  </td>
                  <td>
                    <small class="text-danger">{{ record.reason }}</small>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <nav class="mt-3" v-if="uploadResult.failedRecords.length > pageSize">
              <ul class="pagination pagination-sm justify-content-center">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <button class="page-link" @click="currentPage--" :disabled="currentPage === 1">
                    Previous
                  </button>
                </li>
                <li class="page-item"
                    v-for="page in visiblePages"
                    :key="page"
                    :class="{ active: currentPage === page }">
                  <button class="page-link" @click="currentPage = page">{{ page }}</button>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                  <button class="page-link" @click="currentPage++" :disabled="currentPage === totalPages">
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>

          <!-- Success Only Message -->
          <div v-else-if="uploadResult.successful > 0" class="text-center py-4">
            <i class="bx bx-check-circle text-success" style="font-size: 4rem;"></i>
            <h4 class="mt-3 text-success">All Records Uploaded Successfully!</h4>
            <p class="text-muted">{{ uploadResult.successful }} properties have been added to the system.</p>
          </div>
        </div>

        <div class="modal-footer">
          <button v-if="uploadResult.failed > 0" @click="downloadFailedRecordsCSV" type="button" class="btn btn-danger">
            <i class="bx bx-download me-2"></i>
            Download Failed Records
          </button>
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
            <i class="bx bx-x me-2"></i>
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UploadResultModal',
  props: {
    uploadResult: {
      type: Object,
      default: () => ({
        successful: 0,
        failed: 0,
        totalProcessed: 0,
        message: '',
        failedRecords: []
      })
    }
  },
  data() {
    return {
      currentPage: 1,
      pageSize: 10
    }
  },
  computed: {
    totalPages() {
      if (!this.uploadResult.failedRecords) return 0;
      return Math.ceil(this.uploadResult.failedRecords.length / this.pageSize);
    },
    paginatedFailedRecords() {
      if (!this.uploadResult.failedRecords) return [];
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.uploadResult.failedRecords.slice(start, end);
    },
    visiblePages() {
      const pages = [];
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.totalPages, this.currentPage + 2);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  methods: {
    getStatusBadgeClass(status) {
      const statusMap = {
        'Validation Failed': 'bg-warning',
        'Duplicate': 'bg-info',
        'Insert Failed': 'bg-danger',
        'Exception': 'bg-danger'
      };
      return statusMap[status] || 'bg-secondary';
    },

    downloadFailedRecordsCSV() {
      if (!this.uploadResult.failedRecords || this.uploadResult.failedRecords.length === 0) {
        alert('No failed records to download');
        return;
      }

      // CSV Headers
      const headers = ['Row Number', 'UPN', 'Plot No', 'Owner Names', 'ID Number', 'Phone', 'Status', 'Failure Reason'];

      // Convert failed records to CSV rows
      const csvRows = [
        headers.join(','), // Header row
        ...this.uploadResult.failedRecords.map(record => [
          record.rowNumber,
          this.escapeCSV(record.upn || ''),
          this.escapeCSV(record.plotNo || ''),
          this.escapeCSV(record.ownerNames || ''),
          this.escapeCSV(record.idNo || ''),
          this.escapeCSV(record.ownerPhoneNo || ''),
          this.escapeCSV(record.status || ''),
          this.escapeCSV(record.reason || '')
        ].join(','))
      ];

      // Create CSV content
      const csvContent = csvRows.join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `Failed_Upload_Records_${timestamp}.csv`;

      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    escapeCSV(value) {
      if (value === null || value === undefined) {
        return '';
      }

      const stringValue = String(value);

      // If the value contains comma, quote, or newline, wrap it in quotes and escape internal quotes
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }

      return stringValue;
    }
  },
  watch: {
    uploadResult: {
      deep: true,
      handler() {
        // Reset pagination when new results come in
        this.currentPage = 1;
      }
    }
  }
}
</script>

<style scoped>
.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
}

.table-responsive {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

.pagination-sm .page-link {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.badge {
  font-size: 0.75em;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}
</style>